import type {
  AnimalUpdateInput,
  DogInfoUpsertWithoutAnimalInput,
  MtAnimalBuyerGetAnimalQuery,
} from '@gql/graphql';
import { AnimalTypeEnum } from '@gql/graphql';
import type { BuyerAnimalFormValues } from '@presentation/components';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';
import { useAnimalCreateBuilder } from './useAnimalCreateBuilder';

export const useAnimalUpdateBuilder = () => {
  const { animalCreateBuilder } = useAnimalCreateBuilder();

  const animalUpdateBuilder = useCallback(
    (
      values: Partial<BuyerAnimalFormValues>,
      animal?: NonNullable<MtAnimalBuyerGetAnimalQuery['accountMe']>['animals'][number],
    ) => {
      const data: AnimalUpdateInput = {
        sensitivities: values.sensitivities,
        ...cleanDeep(
          {
            type: { set: values.type ?? AnimalTypeEnum.OTHER },
            name: { set: values.name },
            birthday: { set: values.birthday?.toISOString() },
            birthdayType: { set: values.birthdayType },
            gender: { set: values.gender },
            sterilized: { set: values.sterilized },
            calved: { set: values.calved },
            size: { set: values.size },
          },
          { nullValues: false },
        ),
      };

      if (values.type === AnimalTypeEnum.DOG && values.dogInfo) {
        const {
          data: { dogInfos },
        } = animalCreateBuilder(values);

        data.dogInfos = {
          upsert: {
            create: dogInfos?.create as DogInfoUpsertWithoutAnimalInput['create'],
            update: {
              ...cleanDeep(
                {
                  physicalCondition: { set: values.dogInfo.physicalCondition },
                  pedigree: { set: values.dogInfo.pedigree },
                  pedigreeNumber: { set: values.dogInfo.pedigreeNumber },
                  dogBreed: { connect: { id: values.dogInfo?.breedId } ?? null },
                },
                { nullValues: false },
              ),
            },
          },
        };

        if (data.dogInfos.upsert?.update) {
          // Update medical affections
          if (values.dogInfo?.medicalAffections?.length) {
            // Should probably do this in the backend
            const currentAffections = animal?.dogInfos?.medicalAffections ?? [];
            const formAffectionTypes = values.dogInfo?.medicalAffections ?? [];

            const currentAffectionTypes = currentAffections.map(
              (affection: { affectionType: any }) => affection.affectionType,
            );

            const affectionTypesToCreate = (values.dogInfo.medicalAffections ?? []).filter(
              (x) => !currentAffectionTypes.includes(x),
            );

            const affectionIdsToDelete = currentAffections
              .filter((x) => !formAffectionTypes.includes(x.affectionType))
              .map((x) => x.id);

            data.dogInfos.upsert.update = {
              ...data.dogInfos.upsert.update,
              medicalAffections: {
                deleteMany: [{ id: { in: affectionIdsToDelete } }],
                create: affectionTypesToCreate.map((medicalAffection) => ({
                  affectionType: medicalAffection,
                })),
              },
            };
          }
        }
      }

      return { data };
    },
    [animalCreateBuilder],
  );

  return { animalUpdateBuilder };
};
